// Story saas-1.19 (D13): runtime feature flags with gradual rollout %.
// Story mobile-builder.5: estende o módulo com MobileBuilderFlag (platformMode)
// reaproveitando bucketOf internamente.
//
// Module separado de hooks/usePlan.tsx (que é gating de PLANO Free/Pro/Business).
// Aqui é flag de ATIVAÇÃO de features experimentais com bucketing por userId.
//
// Bucketing determinístico: o mesmo userId sempre cai no mesmo bucket [0, 99],
// então rollout 5% libera o bucket [0, 4] consistentemente. Sem persistência
// por user — derivado puro do ID. Override pra dev/QA via overrides{}.

export type FeatureFlagKey = 'pdf.v2';

interface FeatureFlagSpec {
  /** 0 = off pra todo mundo, 100 = on pra todo mundo. Valores intermediários
   *  habilitam a feature pra `rolloutPercent` % dos userIds via bucketing. */
  rolloutPercent: number;
  /** Override absoluto por userId. Ignora rollout. Ex: dev/QA accounts. */
  forceOn?: ReadonlySet<string>;
  /** Mesmo, mas pra desabilitar. Tem precedência sobre forceOn. */
  forceOff?: ReadonlySet<string>;
}

const FEATURE_FLAGS: Record<FeatureFlagKey, FeatureFlagSpec> = {
  'pdf.v2': {
    rolloutPercent: 0,
  },
};

// Hash determinístico string → [0, 99]. djb2 — boa distribuição uniforme,
// suficiente pra bucketing (não precisa cripto-strength).
function bucketOf(userId: string): number {
  let hash = 5381;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 33) ^ userId.charCodeAt(i);
  }
  return Math.abs(hash) % 100;
}

/**
 * True se a feature está habilitada pro userId dado.
 *
 * Ordem de avaliação:
 *  1. forceOff[userId] → false (kill switch absoluto)
 *  2. forceOn[userId] → true (whitelist absoluto)
 *  3. bucketOf(userId) < rolloutPercent → true (rollout gradual)
 *  4. caso contrário → false
 *
 * Se userId for vazio/anônimo, retorna `rolloutPercent >= 100` (só liga se
 * for full rollout). Evita exposição inconsistente pra users não-autenticados.
 */
export function isFeatureEnabled(key: FeatureFlagKey, userId: string | null | undefined): boolean {
  const spec = FEATURE_FLAGS[key];
  if (!spec) return false;

  if (!userId) {
    return spec.rolloutPercent >= 100;
  }

  if (spec.forceOff?.has(userId)) return false;
  if (spec.forceOn?.has(userId)) return true;

  return bucketOf(userId) < spec.rolloutPercent;
}

/**
 * Retorna o rolloutPercent atual da feature. Usado por telemetria/debug.
 */
export function getRolloutPercent(key: FeatureFlagKey): number {
  return FEATURE_FLAGS[key]?.rolloutPercent ?? 0;
}

// =====================================================
// MOBILE BUILDER FLAG (Story mobile-builder.5)
// =====================================================
//
// Modelo separado do FeatureFlagSpec acima: pdf.v2 usa rolloutPercent simples,
// mobile builder usa platformMode com 4 estados (off/on/canary/percentage). A
// runtime resolution é dinâmica (lido de platform_settings via admin panel)
// enquanto pdf.v2 é compile-time.

export type BuilderSurface = 'mobile' | 'desktop';

export interface MobileBuilderFlag {
  platformMode: 'off' | 'canary' | 'percentage' | 'on';
  canaryAllowlist?: string[];
  percentageBucket?: { percentage: number; seed: string };
}

/**
 * Resolve se o mobile builder deve renderizar pro user dado, baseado no flag.
 * Função PURA — não toca breakpoint, localStorage ou auth. Caller (hook) é
 * responsável pelo resolution order final.
 *
 * Reutiliza `bucketOf` interno combinando `${userId}:${seed}` antes do hash,
 * pra que mudanças no seed redistribuam buckets (canary novo sem afetar pdf.v2).
 *
 * Retornos:
 * - 'enabled' → caller deve usar breakpoint pra decidir mobile|desktop
 * - 'disabled' → caller força desktop
 * - null → flag indeterminado (ex: row ausente, ainda carregando); caller
 *   trata como 'enabled' e cai no breakpoint
 *
 * Comportamento por platformMode:
 * - 'off' → 'disabled' (sempre desktop, ignora user)
 * - 'on' → 'enabled' (libera pra todos; user pode ser null)
 * - 'canary' → 'enabled' se user.email ∈ allowlist; senão 'disabled'
 * - 'percentage' → 'enabled' se bucketOf(`${userId}:${seed}`) < percentage
 *
 * canary e percentage exigem user identificado; sem user → 'disabled'.
 */
export function resolveMobileBuilderSurface(
  flag: MobileBuilderFlag | null | undefined,
  user: { id: string; email: string } | null,
): 'enabled' | 'disabled' | null {
  if (!flag) return null;
  if (flag.platformMode === 'off') return 'disabled';
  if (flag.platformMode === 'on') return 'enabled';
  if (!user) return 'disabled';
  if (flag.platformMode === 'canary') {
    return flag.canaryAllowlist?.includes(user.email) ? 'enabled' : 'disabled';
  }
  if (flag.platformMode === 'percentage' && flag.percentageBucket) {
    const { percentage, seed } = flag.percentageBucket;
    return bucketOf(`${user.id}:${seed}`) < percentage ? 'enabled' : 'disabled';
  }
  return 'disabled';
}
